import { get, sortBy } from 'lodash-es';
import { GwDatesWrapper } from '../../../components/_internal_date_/gwDatesWrapper';
import { addOnsMapperNewAPIToOldApi } from '../../../pages/ReservationDetail/utils';
import { getTheFirstElementOfArray } from '../../../utilities/arraysUtils';
import { EReservationStatus } from '../../../utilities/availabilityUtils';
import {
  ANYTIME_TYPE,
  API_STATUS,
  CABANAS_CODES_TYPES,
  CHECKOUT_TIMES,
  DATE_FORMATS,
  DAY_PASSES_TYPE,
  HOWL_N_LEARN_CODE,
  LATE_CHECKOUT_PREFIXES,
  PACKAGE_TYPE,
  WEEKDAY_TYPE
} from '../../../utilities/constants';
import { comparePackageTypes } from '../../../utilities/packageTypeUtils';

/**
 * Types of reservations.
 * @enum {string}
 */
export const RESERVATION_TYPES = {
  suite: 'suite',
  dayPass: 'dayPass',
  cabana: 'cabana',
  seasonPass: 'seasonPass'
};

export const getManageReservationDetails = state => state?.manageReservationDetails;

export const getCanCancel = state => state?.data?.canCancel;

export const getCanHoldRoomCredits = state => state?.data?.canHoldRoomCredits;

export const getCanAddGuests = state => state?.data?.canAddGuests;

export const getProduct = state => state?.data?.product;

export const getGuests = state => state?.data?.guests;

export const getOfferCode = state => state?.data?.offerCode;

export const getArrival = state => state?.data?.arrival;

export const getIsLoading = state => state?.manageReservationApiStatus === API_STATUS.LOADING;

export const getDeparture = state => state?.data?.departure;

export const getReservationId = state => state?.data?.id;

export const getResortFee = state => state?.data?.costSummary?.taxesAndFees?.RESORT;

export const getTotal = state => state?.data?.costSummary?.total;

export const getUpsellAddOns = state => {
  return state.manageReservationDetails?.upsellAddOns ?? [];
};

export const getComparison = state => {
  return state?.data?.comparisonChanges;
};

export const getComparisonPaymentInfo = state => state?.data?.comparisonChanges?.payment;

export const getChangeFee = state => state?.data?.costSummary?.taxesAndFees?.RESERVATION_CHANGE_FEE;

export const getUpsellAddOnByType = (state, packageType) => {
  const upsellAddOns = state.manageReservationDetails?.upsellAddOns ?? [];
  const cardTile = upsellAddOns.filter(pck => pck.type?.includes(packageType));
  const addOnsWithContent = cardTile[0]?.addOns
    ? addOnsMapperNewAPIToOldApi(getTheFirstElementOfArray(cardTile)?.addOns, packageType)
    : [];
  return addOnsWithContent;
};

export const getIsPackagesUpdated = state => {
  return state.packagesUpdated;
};

/**
 * Get the value of requestSuccess.
 * @param {Object} state reservationDetails entity store.
 * @return {boolean} True if we succesfully fetched data.
 */
export const getRequestSuccess = state => state?.manageReservationApiStatus === API_STATUS.SUCCEEDED;

export const getDynamicTile = state => {
  return state.dynamicTile;
};

/**
 * Get the error message.
 * @param {Object} state ReservationDetails entity store.
 * @return {string} Error message
 */
export const getError = state => state?.errorMessage;

/**
 * Get the special request from reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {Object} Birth Day Celebration Special Request.
 */
export const getBirthDayCelebration = state => state?.data?.specialRequests?.celebrations;

/**
 * Get the special request from reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {array.<Object>} List of special request added.
 */
export const getSpecialRequests = state => state?.data?.specialRequests;

/**
 * Get the preferences from reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {array.<Object>} List of preferences added.
 */
export const getPreferences = state => state?.data?.specialRequests?.preferences;

/**
 * Get comments from reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {array.<Object>} List of comments.
 */
export const getComments = state => state?.data?.specialRequests?.comment;

/**
 * Get the reservation status.
 * @param {Object} state Reservations store.
 * @return {string} Confirmed reservation status.
 */
export const getStatus = state => state?.data?.status;

export const getPackageErrorMessage = state => {
  return state?.packageErrorMessageData;
};

/**
 * Get packages added to the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {array.<Object>} List of packages added.
 */
export const getPackages = state => {
  return get(state, 'data.addOns', []);
};

/**
 * Get the room type.
 * @param {Object} state Reservations store.
 * @return {string} Confirmed reservation room type.
 */
export const getRoomType = state => {
  return get(state, 'data.product.code') ?? get(state, 'data.roomType');
};

/**
 * Returns if is a suite, day pass or a cabana reservation.
 * @param {Object} state Reservations store.
 * @return {String} Type of reservation
 */
export const getReservationType = state => {
  const roomType = getRoomType(state);
  const dayPassesCodes = Object.values(DAY_PASSES_TYPE);
  const cabanaCodes = Object.values(CABANAS_CODES_TYPES);
  if (dayPassesCodes.includes(roomType)) {
    return RESERVATION_TYPES.dayPass;
  } else if (cabanaCodes.includes(roomType)) {
    return RESERVATION_TYPES.cabana;
  } else if (roomType === 'SEASON') {
    return RESERVATION_TYPES.seasonPass;
  }

  return RESERVATION_TYPES.suite;
};

/**
 * Check if reservation is using free day pass voucher (WPVCR rate code).
 * @param {Object} state Reservations store.
 * @return {boolean} True if using WPVCR rate code for day pass
 */
export const getIsFreeVoucherDayPass = state => {
  const reservationType = getReservationType(state);
  return reservationType === RESERVATION_TYPES.dayPass && state.data.isFreeVoucher;
};

/**
 * Get the site for reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {string} Confirmed reservation site.
 */
export const getSite = state => {
  return state?.data?.lodge?.code ?? '';
};

/**
 * Get firstName (users) for the reservation.
 * @return {String} - Reservation guest's first name;
 */
export const getFirstName = state => {
  return state.data?.contactInfo?.firstName ?? '';
};

export const getCostSummarySuiteName = state => {
  return state?.data?.product?.name ?? '';
};

/**
 * Get the number of adults.
 * @param {Object} state Reservations store.
 * @return {number} Confirmed reservation number of adults.
 */
export const getAdults = state => {
  return state.data?.guests?.adults ?? 0;
};

/**
 * Get the number of kid(s).
 * @param {Object} state Reservations store.
 * @return {number} Confirmed reservation number of children.
 */
export const getKids = state => {
  return state.data?.guests?.children?.length ?? 0;
};

/**
 * Get the kid(s) age(s).
 * @param {Object} state Reservations store.
 * @return {array.<String>} Confirmed reservation number of children.
 */
export const getKidsAges = state => {
  return state.data?.guests?.children ?? [];
};

/**
 * Get the change dates fee.
 * @param {Object} state Reservations store.
 * @return {number} Object that contains {amount: number, applies: boolean}.
 */
export const getChangeDatesFee = state => {
  return state.data?.changeFee;
};

/**
 * Get the checkout time selected for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {String} - String containing the checkout time.
 */
export const getCheckoutTime = state => {
  const reservation = get(state, 'data');
  let checkoutTime = CHECKOUT_TIMES.AM_11;
  if (reservation) {
    if (reservation?.addOns?.length > 0) {
      const lateCheckoutPkg = reservation.addOns.find(
        pkg => pkg.code?.includes(LATE_CHECKOUT_PREFIXES.code) || pkg.code?.includes(HOWL_N_LEARN_CODE)
      );

      if (lateCheckoutPkg) {
        checkoutTime = CHECKOUT_TIMES.PM_2;
      }
    }
    const { packages } = reservation;
    if (packages) {
      const lateCheckoutPkg = packages.find(
        pkg => pkg.packageCode.includes(LATE_CHECKOUT_PREFIXES.code) || pkg.packageCode.includes(HOWL_N_LEARN_CODE)
      );

      if (lateCheckoutPkg) {
        checkoutTime = CHECKOUT_TIMES.PM_2;
      }
    }
  }
  return checkoutTime;
};

/**
 * Get the number of nights for reservation.
 * @param {Object} state Reservations store.
 * @return {number} Confirmed reservation number of nights.
 */
export const getNumberOfNights = state => {
  return state?.data?.duration ?? 0;
};

/**
 * Get the total room rate.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - Total room rate of confirmed suite for the reservation.
 */
export const getTotalRoomRate = state => {
  return state?.data?.product?.totalPrice ?? 0;
};

export const getSeasonPassUnitPrice = state => {
  return state?.data?.costSummary?.unitPrice ?? 0;
};

/**
 * Get cabanas added in the same days of the reservation.
 * @param {Object} state reservationDetails entity store.
 * @returns {array.<Object>} List of cabanas added in the same days of the reservation.
 */
export const getCabanas = state => {
  return (
    state?.data?.addOns?.filter(
      item =>
        comparePackageTypes(item.type, PACKAGE_TYPE.cabanasAndFlexTrip) ||
        comparePackageTypes(item.type, PACKAGE_TYPE.favorite)
    ) ?? []
  );
};

export const getIsFullDayPass = state => {
  return get(state, 'data.product.code') === DAY_PASSES_TYPE.fullDay;
};

export const getRoomUpgradeInfo = state => {
  return state?.data?.roomUpgrade;
};

export const getCanChangeDates = state => {
  return state?.data?.canChangeDates;
};

/**
 * Get sutie details for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {Object} - Suite details[Suite name, adults count, kids count, infants count, offer code] of the confirmed suite.
 */
export const getSuiteDetails = (state, includePropertyAndRoomType) => {
  const reservation = get(state, 'data');
  let suiteDetail = {};
  if (reservation) {
    if (reservation?.isCabana) {
      suiteDetail.title = reservation.roomDescription;
    }

    // Adding total room rate for selected suite
    if (reservation?.rateInfo) {
      const { totalRoomRate } = reservation.rateInfo;
      suiteDetail.totalRoomRate = totalRoomRate;
    } else {
      suiteDetail.totalRoomRate = reservation?.product?.totalPrice;
    }

    // Setting guests count.
    if (reservation?.selection) {
      const { numAdults, numChildrenOverTwo, numChildren } = reservation.selection;
      suiteDetail.adults = numAdults;
      suiteDetail.kids = numChildrenOverTwo;
      suiteDetail.totalKids = numChildren;
    } else {
      suiteDetail.adults = reservation?.guests?.adults;
      suiteDetail.kids = reservation?.guests?.children ? reservation.guests.children.filter(kid => kid > 2).lenght : 0;
      suiteDetail.totalKids = reservation?.guests?.children ? reservation.guests.children.length : 0;
    }

    suiteDetail.offerCode = reservation.offerCode;
    if (includePropertyAndRoomType) {
      suiteDetail.property = reservation.property || reservation?.lodge?.code;
      suiteDetail.roomType = reservation.roomType;
    }
  }
  return suiteDetail;
};

/**
 * Get the parking fee for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - Parking fee for the reservation.
 */
export const getParkingFee = state => {
  return state?.data?.costSummary?.taxesAndFees?.PARKING ?? 0;
};

/**
 * Get the deposit paid for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - deposit paid for the reservation.
 */
export const getDepositPaid = state => {
  const roomDeposit = state?.data?.costSummary?.depositPaid ?? 0;
  const cabanaDeposit = state?.data?.cabanasDepositAmount ?? 0;
  return roomDeposit + cabanaDeposit;
};

/**
 * Get the Sustainability  fee for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - Resort fee for the reservation.
 */
export const getSustainabilityFee = state => {
  return state?.data?.costSummary?.taxesAndFees?.SUSTAINABILITY ?? 0;
};

/**
 * Get the number of billable kids.
 * @param {Object} state Reservations store.
 * @return {number} Confirmed reservation number of billable kids.
 */
export const getBillableGuest = state => {
  const children = state?.data?.guests?.children ?? [];
  const numChildrenOverTwo = children ? children.filter(kid => kid > 2).length : 0;
  return getAdults(state) + numChildrenOverTwo;
};

export const getPrivateLodgingFee = state => {
  return state?.data?.costSummary?.taxesAndFees?.PRIVATE_LODGING ?? 0;
};

export const getTaxesAndFeesForReservation = state => {
  return state?.data?.costSummary?.taxesAndFees;
};

export const getPointsDiscountApplied = state => {
  return state?.data?.costSummary?.totalPoints ?? 0;
};

export const getOffersDiscountApplied = state => {
  return state?.data?.costSummary?.totalOffers ?? 0;
};

export const getSuppressRate = state => {
  return state?.data?.costSummary?.suppressRate ?? 0;
};

export const getCabanaDeposit = state => {
  return state?.data?.cabanasDepositAmount ?? 0;
};

/**
 * Get the room deposit paid for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - deposit paid for the reservation.
 */
export const getRoomDeposit = state => {
  return state?.data?.costSummary?.depositPaid ?? 0;
};

/**
 * Get the number of adults and kids.
 * @param {Object} state Reservations store.
 * @return {number} Confirmed reservation number of adults and kids.
 */
export const getTotalBillAndNonBillGuest = state => {
  return getAdults(state) + getKids(state);
};

export const getRateType = state => {
  return state?.data?.rateType || '';
};

export const getIsDynamicOfferCode = state => {
  return state?.data?.isDynamicOfferCode ?? false;
};

export const getAddGuestReservationErrorMessage = state => {
  return state.addGuestReservationErrorMessage;
};

/**
 * Get the whole reservation object
 * @param {Object} state
 */
export const getReservationData = state => {
  return state?.data;
};

/**
 * Get lastName (users) for the reservation.
 * @return {String} - Arrival Date;
 */
export const getLastName = state => {
  return state.data?.contactInfo?.lastName;
};

export const getIsCabanaLoading = state => {
  return state.cabanaApiStatus === API_STATUS.LOADING;
};

export const getCabanaErrorMessage = state => {
  return state.cabanaErrorMessage;
};

export const getCabanaRequestSuccess = state => {
  return state.cabanaApiStatus === API_STATUS.SUCCEEDED;
};

export const getReservationAddress = state => {
  return state?.data?.payment?.billingAddress ?? {};
};

export const getPassTableList = (state, packageType) => {
  const upsellAddOns = state?.upsellAddOns ?? [];

  const packageTable = upsellAddOns.filter(pck => pck.type === packageType);

  const passTableList = getTheFirstElementOfArray(packageTable)?.passTable ?? [];
  return passTableList;
};

export const getIsSeasonpass = state => {
  const reservationType = getReservationType(state);
  return reservationType.toUpperCase() === RESERVATION_TYPES.seasonPass.toUpperCase();
};

export const getIsDaypass = state => {
  const reservationType = getReservationType(state);
  return reservationType.toUpperCase() === RESERVATION_TYPES.dayPass.toUpperCase();
};

export const getIsDaypassWithSeasonPass = state => {
  return getIsDaypass(state) && Boolean(state?.data?.seasonPass?.id);
};

export const getIsSeasonPassWithDaypassReserved = state => {
  return getIsSeasonpass(state) && Boolean(state?.data?.seasonPass?.dayPasses?.length > 0);
};

export const getDaypassFromSeasonDetails = state => {
  return getTheFirstElementOfArray(state?.data?.seasonPass?.dayPasses);
};

export const getSeasonpassFromReservation = state => state?.data?.seasonPass;
export const getSeasonPassStartDate = state => state?.data?.seasonPass?.startDate;
export const getSeasonPassEndDate = state => state?.data?.seasonPass?.endDate;
export const getSeasonPassType = state => state?.data?.seasonPass?.type;

export const getFormattedSeasonPassType = state => {
  const passType = getSeasonPassType(state);
  if (passType?.toUpperCase() === WEEKDAY_TYPE) return 'Weekday';
  else if (passType?.toUpperCase() === ANYTIME_TYPE) return 'Anytime';
  return '';
};

export const getSeasonPassId = state => state?.data?.seasonPass?.id;

export const getIsExpiredSeasonPass = state => {
  const seasonPass = state?.data?.seasonPass;
  if (!getIsSeasonpass(state) || !seasonPass) return false;
  return !seasonPass.canRedeem && GwDatesWrapper.isBefore(seasonPass.endDate, new Date());
};

export const getSeasonPassContactInfo = state => state?.data?.contactInfo;
export const getSeasonpassReservationPrimaryHolder = state =>
  state?.data?.seasonPass?.holders?.find(holder => holder.role === 'primary');
export const getSeasonpassReservationSecondaryHolder = state =>
  state?.data?.seasonPass?.holders?.find(holder => holder.role === 'secondary');

/**
 * Get the taxes amount including cabanas.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - Taxes amount for the reservation including cabanas.
 */
export const getTaxesAmountWithCabanas = state => {
  const taxesAmount = get(state, 'data.costSummary.taxesAndFees.TAX') ?? get(state, 'data.rateInfo.taxes');

  const cabanas = get(state, 'data.cabanas');
  let totalTaxesCabanas = 0;
  if (cabanas) {
    totalTaxesCabanas = cabanas
      .filter(c => c.status !== EReservationStatus.CANCELED)
      .map(c => c.totalTaxes)
      .reduce((total, roomRate) => {
        return total + roomRate;
      }, 0);
  }
  return taxesAmount + totalTaxesCabanas;
};

/**
 * Get the summary total for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - Summary total for the reservation.
 */
export const getSummaryTotal = state => {
  return get(state, 'data.costSummary.total') ?? get(state, 'data.rateInfo.total');
};

/**
 * Gets the summary total for the reservation including cabanas.
 * @param {Object} state reservationDetails entity store.
 * @returns {number} - Summary total for the reservation including cabanas.
 */
export const getSummaryTotalWithCabana = state => {
  const cabanas = get(state, 'data.cabanas');
  let totalCabanas = 0;
  if (cabanas) {
    totalCabanas = cabanas
      .filter(c => c.status !== EReservationStatus.CANCELED)
      .map(c => c.total)
      .reduce((total, totalCabanaWithTaxes) => {
        return total + totalCabanaWithTaxes;
      }, 0);
  }
  return getSummaryTotal(state) + totalCabanas;
};

/**
 * Get total room rate plus packages rate.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - Total room rate plus packages.
 */
export const getTotalRoomRateAndPackages = state => {
  return (
    get(state, 'data.costSummary.totalRoomRateAndPackages', 0) ??
    get(state, 'data.rateInfo.totalRoomRateAndPackages', 0)
  );
};

/**
 * Get the package total for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - Total amount of the packages selected.
 */
export const getPackageTotal = state => {
  return getTotalRoomRateAndPackages(state) - getTotalRoomRate(state);
};

/**
 * Get the package total with cabanas for the reservation.
 * @param {Object} state reservationDetails entity store
 * @returns {number} Total sum of amounts for packages including cabanas
 */
export const getPackageTotalWithCabanas = state => {
  const cabanas = get(state, 'data.cabanas');
  let totalCabanas = 0;
  if (cabanas) {
    totalCabanas = cabanas
      .filter(c => c.status !== EReservationStatus.CANCELED)
      .map(c => c.totalRoomRate)
      .reduce((total, roomRate) => {
        return total + roomRate;
      }, 0);
  }
  return getPackageTotal(state) + totalCabanas;
};

/**
 * Get the deposit paid for the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {number} - deposit paid for the reservation.
 */
export const getDepositePaid = state => {
  const roomDeposit = get(state, 'data.costSummary.depositPaid')
    ? get(state, 'data.costSummary.depositPaid')
    : get(state, 'data.advancedDeposit', get(state, 'data.rateInfo.advancedDeposit', 0));
  const cabanaDeposit = get(state, 'data.cabanasDepositAmount') ? get(state, 'data.cabanasDepositAmount') : 0;
  return roomDeposit + cabanaDeposit;
};

/**
 * Get the due at Checking amount for the reservation including cabanas.
 * @param {Object} state reservationDetails entity store.
 * @returns {number} - Due at Checkin amount for the reservation.
 */
export const getDueAtCheckinAmountWithCabanas = state => {
  const totalWithCabana = Number(getSummaryTotalWithCabana(state).toFixed(2));
  const totalDepositPaid = Number(getDepositePaid(state).toFixed(2));
  return totalWithCabana - totalDepositPaid;
};

export const getCabanasAsPackages = state => {
  // Check if this is cabana object is present.
  const cabanasEntity = get(state, 'data.cabanas', []);
  if (!cabanasEntity) {
    return;
  }

  const cabanasFiltered = cabanasEntity.filter(c => c.status !== EReservationStatus.CANCELED);
  const cabanas = sortBy(cabanasFiltered, item => GwDatesWrapper.format(item.insertedAt, DATE_FORMATS.numberFormat), [
    'asc'
  ]);
  return cabanas.reduce((cabanasAcc, currentCabana, index) => {
    const cabanaToAdd = {
      description: currentCabana.roomDescription,
      shortDescription: currentCabana.roomDescription,
      endDate: currentCabana.arrival,
      startDate: currentCabana.arrival,
      packageAmount: currentCabana.totalRoomRate,
      packageCode: index === cabanas.length - 1 ? currentCabana.roomType : `${currentCabana.roomType}_${index}`,
      quantity: 1,
      tax: currentCabana.totalTaxes,
      totalAmount: currentCabana.total,
      unitAmount: currentCabana.totalRoomRate,
      isCabana: true,
      totalRoomRateAndPackages: currentCabana.totalRoomRateAndPackages
    };
    cabanasAcc.push(cabanaToAdd);
    return cabanasAcc;
  }, []);
};

export const getRecommendedModalPackage = state => state?.recommendedAddOns ?? null;

/**
 * Get the suite upgrade offer groups, in the exact order returned by the API (no client re-sort).
 * @param {Object} state reservationDetails entity store.
 * @return {array.<Object>} List of suite upgrade offer groups.
 */
export const getSuiteUpgradeOffers = state => state?.suiteUpgradeOffers ?? [];

/**
 * Get the guest's current (booked) suite details returned alongside the upgrade offers.
 * @param {Object} state reservationDetails entity store.
 * @return {Object|null} Current suite details, or null when unavailable.
 */
export const getSuiteUpgradeCurrentSuite = state => state?.suiteUpgradeCurrentSuite ?? null;

/**
 * Whether the reservation can be upgraded in-app. Gates every CMP entry surface (callout tag,
 * room-type card CTA, manage-modal option).
 *
 * Reads `canUpgradeSuite` from the reservation itself rather than `isEligible` from the offers
 * endpoint: the reservation response houses everything the CMP needs up front, so the offers
 * endpoint is only called once the guest actually opens the drawer.
 *
 * When this is false the room-type card falls back to its existing Nor1 link, which is driven
 * separately by `roomUpgrade.link` via checkUpdateSuiteDetails.
 * @param {Object} state reservationDetails entity store.
 * @return {boolean} True when the in-app upgrade offers should be surfaced.
 */
export const getIsSuiteUpgradeAvailable = state => Boolean(state?.data?.canUpgradeSuite);

/**
 * Whether the loaded reservation was upgraded in this session, for the "New" tag on the room-type
 * card.
 * @param {Object} state reservationDetails entity store.
 * @return {boolean} True when the reservation on screen was just upgraded.
 */
export const getWasSuiteUpgraded = state => Boolean(state?.isSuiteUpgraded);

/**
 * Lowest per-night upgrade delta, for "Upgrades available from $X/night". The backend guarantees
 * `roomUpgrade.price` is always the cheapest available upgrade, so it is read rather than derived
 * from the offers list. Note the API sends it as a string (e.g. "15.3").
 * @param {Object} state reservationDetails entity store.
 * @return {string|number|null} Cheapest per-night delta, or null when there is no upgrade.
 */
export const getSuiteUpgradeFromPrice = state => state?.data?.roomUpgrade?.price ?? null;

export const getIsSuiteUpgradeOffersLoading = state => state?.suiteUpgradeOffersStatus === API_STATUS.LOADING;

/**
 * Whether the offers request failed. The drawer must tell a failure apart from a genuinely empty
 * list: the entry points are gated on the reservation's canUpgradeSuite, so a guest who was shown
 * "Upgrades available from $X/night" can still open the drawer onto a failed fetch, and must not be
 * told that no upgrades exist.
 * @param {Object} state reservationDetails entity store.
 * @return {boolean} True when the offers request errored.
 */
export const getIsSuiteUpgradeOffersError = state => state?.suiteUpgradeOffersStatus === API_STATUS.ERROR;

/**
 * Get the costed preview for the selected upgrade suite. Holds the pricingKey (price-lock) and
 * the server-resolved ruleId that the commit step must echo back.
 * @param {Object} state reservationDetails entity store.
 * @return {Object|null} Preview breakdown, or null before it resolves.
 */
export const getSuiteUpgradePreview = state => state?.suiteUpgradePreview ?? null;

export const getIsSuiteUpgradePreviewLoading = state => state?.suiteUpgradePreviewStatus === API_STATUS.LOADING;

export const getIsSuiteUpgradePreviewError = state => state?.suiteUpgradePreviewStatus === API_STATUS.ERROR;

export const getIsSuiteUpgradeCommitting = state => state?.suiteUpgradeCommitStatus === API_STATUS.LOADING;

export const getSuiteUpgradeCommitError = state => state?.suiteUpgradeCommitError ?? null;

export const getSuiteUpgradeCommitErrorCode = state => state?.suiteUpgradeCommitErrorCode ?? null;

/**
 * Get bundle package added to the reservation.
 * @param {Object} state reservationDetails entity store.
 * @return {array.<Object>} List of bundle package data if added.
 */
export const getBundlePackage = state => {
  return state?.data?.addOns?.filter(item => comparePackageTypes(item.type, PACKAGE_TYPE.bundle)) ?? null;
};

export const getSummaryAlert = state => state?.data?.alert ?? null;

export const getGuestSplash = state => state?.data?.guests?.splash ?? null;

export const getDailyParkingFee = state => state?.data?.lodge?.dailyParkingFee ?? null;
